import FJNetworkStore from '../../store/FJNetworkStore';
import FJImageStore from '../../store/FJImageStore';
import FJCloudTranscodingUtil from '../../util/FJCloudTranscodingUtil';
import FJMessageStore from '../../store/FJMessageStore';
import FJLocalStore from '../../store/FJLocalStore';
import FJConstant from '../../constants/FJConstant';
import {
    setTranscodingStatus,
    addTranscodingTask,
    updateTranscodingTask,
    updateTranscodingConfig,
    resetTranscodingState,
    setCloudTranscodingCallbacks,
} from '../slices/FJSliceCloudTranscoding';
import FJUtil from '../../util/FJUtil';
import { FJCloudTranscodingConfig } from '../../config/FJCloudTranscodingConfig';
import FJS3Config from '../../config/FJS3Config';
import FJActionCommon from './FJActionCommon';
import FJActionModal from './FJActionModal';
import FJFileHandleUtil from '../../util/FJFileHandleUtil';

const MAX_POLLING_ERROR_TIME = 5;
let f_timer = null;
let f_errorTime = 0;
let f_transcode_isPolling = false;
let f_cancelled = false;
const normal_time = 1000 * 2;
const error_time = 1000 * 30;

/**
 * 云转码业务逻辑Action
 * 提供转码任务的创建、监控、管理等功能
 */
const FJActionCloudTranscoding = {
    /**
     * 处理单个文件的转码流程（内部方法）
     * 负责上传文件到S3并创建转码任务，不负责检查任务是否已存在和轮询状态
     * @param {File} fileInput - 文件对象
     * @param {string} tempId - 临时任务ID
     * @param {string} [precomputedId] - 预计算的文件ID，避免重复计算（可选）
     * @param {string} [precomputedFileExtension] - 预计算的文件扩展名，避免重复计算（可选）
     * @returns {Function} Redux thunk action，返回包含 tempId 和 eventId 的对象
     */
    _processFileWithTranscodingAction: function (
        fileInput,
        tempId,
        precomputedId = null,
        precomputedFileExtension = null,
    ) {
        return async dispatch => {
            try {
                FJUtil.eaLog('ai_tool_cloud_transcode_format', { format: fileInput.type });

                // 处理文件输入
                let s3Key;
                let s3TranscodingUrl;
                let fileExtension;
                let id;

                // 如果已预计算，使用预计算的值，否则重新计算
                if (precomputedId && precomputedFileExtension) {
                    id = precomputedId;
                    fileExtension = precomputedFileExtension;
                } else {
                    fileExtension = FJCloudTranscodingUtil.getFileExtensionFromFile(fileInput);
                    id = await FJUtil.hashEncode(fileInput);
                }

                // 生成S3 key
                s3Key = FJCloudTranscodingConfig.cloudTranscodingInputKey + `${id}.${fileExtension}`;

                if (f_cancelled) {
                    return;
                }

                // 上传文件到S3
                if (fileInput instanceof File) {
                    const uploadResult = await FJImageStore.upLoadFileToS3Async(fileInput, s3Key);
                    s3TranscodingUrl = FJS3Config.S3_NOT_CATCH_HOST + s3Key;

                    if (!uploadResult) {
                        const err = new Error('Failed to upload file to S3');
                        err.cause = FJConstant.SOURCE_LOADING_ERROR;
                        throw err;
                    }
                } else {
                    const err = new Error('Invalid file input');
                    err.cause = FJConstant.SOURCE_LOADING_ERROR;
                    throw err;
                }

                // 创建新的转码任务
                if (f_cancelled) {
                    return;
                }

                const _request = async () => {
                    return await FJNetworkStore.createTranscodeTask(s3TranscodingUrl);
                };

                const eventId = await FJActionCommon.createRequestTask(_request, dispatch);

                if (!eventId) {
                    const err = new Error('Failed to create transcoding task');
                    err.cause = FJConstant.ERROR;
                    throw err;
                }

                // 更新任务状态到Redux（不触发轮询，由调用方统一处理）
                const taskData = {
                    tempId,
                    id: eventId,
                    progress: 10,
                    status: FJConstant.RUNNING,
                };

                dispatch(updateTranscodingTask({ updates: [taskData] }));

                // 返回任务信息，供调用方使用
                return { tempId, eventId };
            } catch (error) {
                console.error('Transcoding process error:', error);
                dispatch(
                    updateTranscodingTask({ updates: [{ tempId: tempId, status: FJConstant.ERROR, progress: 0 }] }),
                );
                throw error;
            }
        };
    },

    /**
     * 取消转码任务
     * @param {string} taskId - 任务ID
     * @returns {Function} Redux thunk action
     */
    cancelTranscodingTaskAction: function (taskIds) {
        return async dispatch => {
            try {
                // 创建前取消转码任务
                if (!taskIds || taskIds.length === 0) {
                    f_cancelled = true;
                    return;
                }
                const { code } = await FJNetworkStore.delCloudTranscodeTask(taskIds);

                if (code === 200) {
                    f_cancelled = true;
                    dispatch(
                        updateTranscodingTask({
                            updates: taskIds.map(taskId => ({ id: taskId, status: FJConstant.CANCELLED, progress: 0 })),
                        }),
                    );
                }
            } catch (error) {
                console.error('Error cancelling transcoding task:', error);
                FJMessageStore.addMessage(FJLocalStore._('failed-to-cancel-transcoding'), 'error');
                return false;
            }
        };
    },

    /**
     * 更新转码配置
     * @param {Object} config - 配置对象
     * @returns {Function} Redux thunk action
     */
    updateTranscodingConfigAction: function (config) {
        return dispatch => {
            dispatch(updateTranscodingConfig(config));
        };
    },

    setCloudTranscodingCallbacksAction: function (callbacks) {
        return dispatch => {
            dispatch(setCloudTranscodingCallbacks(callbacks));
        };
    },

    /**
     * 重置转码状态
     * @returns {Function} Redux thunk action
     */
    resetTranscodingStateAction: function () {
        return dispatch => {
            dispatch(resetTranscodingState());
            clearTimeout(f_timer);
            f_timer = null;
            f_errorTime = 0;
            f_transcode_isPolling = false;
            f_cancelled = false;
        };
    },

    /**
     * 批量转码文件
     * 流程：1. 批量检查已存在任务 2. 分类处理（已存在/需转码） 3. 统一创建转码任务 4. 统一开始轮询
     * @param {Array<File>} fileInputs - 文件对象数组
     * @param {Object} options - 转码选项
     * @param {string} options.mediaType - 媒体类型 ('video', 'audio', 'image')
     * @param {string} options.outputFormat - 输出格式
     * @returns {Function} Redux thunk action
     */
    batchCloudTranscodeFilesAction: function (fileInputs, options = {}) {
        return async (dispatch, getState) => {
            const tool = FJUtil.getToolName();
            const isLogin = FJUtil.isLogin();
            if (!isLogin) {
                FJUtil.login();
                FJActionModal.hideCloudTranscodingModalAction();
                return;
            }

            const { mediaType = 'video' } = options;

            // 分类处理文件：支持转码的文件和不支持的文件
            const supportedFiles = [];
            const unsupportedFiles = [];
            const mediaTypeMismatchFiles = [];

            fileInputs.forEach(file => {
                // 检测文件的实际媒体类型
                const detectedMediaType = FJCloudTranscodingUtil.detectMediaType(file);

                if (detectedMediaType === mediaType) {
                    // 媒体类型匹配，支持转码
                    supportedFiles.push(file);
                } else if (detectedMediaType) {
                    // 媒体类型不匹配，但文件格式支持转码
                    mediaTypeMismatchFiles.push(file);
                } else {
                    // 完全不支持的文件格式
                    unsupportedFiles.push(file);
                }
            });

            // 处理媒体类型不匹配的文件 - 显示toast并过滤
            if (mediaTypeMismatchFiles.length > 0) {
                FJMessageStore.addMessage(FJLocalStore._('unsupported-media-type-for-image'), 'error');
            }

            // 处理不支持的文件格式 - 设置为失败任务
            const failedTasks = unsupportedFiles.map(file => ({
                tempId: `transcode-${Date.now()}-${Math.random()}`,
                fileName: file.name,
                mediaType: options.mediaType,
                outputFormat: options.outputFormat,
                file: file,
                status: FJConstant.ERROR,
                progress: 100,
            }));

            // 创建支持转码的任务列表
            const tempTaskList = supportedFiles.map(item => {
                return {
                    tempId: `transcode-${Date.now()}-${Math.random()}`,
                    fileName: item.name,
                    mediaType: options.mediaType,
                    outputFormat: options.outputFormat,
                    file: item,
                    status: FJConstant.RUNNING,
                    progress: 0,
                };
            });

            // 添加所有任务（包括失败的任务）到Redux状态
            const allTasks = [...tempTaskList, ...failedTasks];
            dispatch(addTranscodingTask(allTasks));

            // 只对支持转码的文件执行转码
            if (tempTaskList.length > 0) {
                FJUtil.eaLog('ai_tool_cloud_transcode_start', { tool });

                // 重置状态
                dispatch(setTranscodingStatus(FJConstant.RUNNING));

                // 步骤1: 批量检查所有文件是否已存在
                // 为所有文件生成检查所需的 remoteFileName、id 和 fileExtension
                const filesToCheck = await Promise.all(
                    tempTaskList.map(async (task, index) => {
                        const file = supportedFiles[index];
                        const fileExtension = FJCloudTranscodingUtil.getFileExtensionFromFile(file);
                        const id = await FJUtil.hashEncode(file);
                        return {
                            task,
                            file,
                            remoteFileName: `${id}.${fileExtension}`,
                            id,
                            fileExtension,
                        };
                    }),
                );

                // 分类存储：已存在的任务和需要转码的任务
                let existingTasks = [];
                let tasksToTranscode = [];

                if (f_cancelled) {
                    return;
                }

                try {
                    // 批量检查已存在的转码任务
                    const remoteFileNames = filesToCheck.map(item => item.remoteFileName);
                    const { data: checkResultData } = await FJNetworkStore.checkCloudTranscodeIsExist(remoteFileNames);

                    // 根据检查结果分类处理：已存在的任务和需要转码的任务
                    filesToCheck.forEach(({ task, file, remoteFileName, id, fileExtension }) => {
                        if (checkResultData.exists && checkResultData.exists[remoteFileName]) {
                            // 任务已存在，构造已存在的S3 URL并准备更新状态
                            const existingS3Url =
                                FJS3Config.S3_NOT_CATCH_HOST +
                                FJCloudTranscodingConfig.cloudTranscodingExportKey +
                                checkResultData.exists[remoteFileName];

                            const existingTaskData = {
                                tempId: task.tempId,
                                status: FJConstant.SUCCESS,
                                progress: 100,
                                downloadUrl: existingS3Url,
                            };

                            existingTasks.push(existingTaskData);
                        } else {
                            // 任务不存在，需要转码，保存预计算的 id 和 fileExtension 避免重复计算
                            tasksToTranscode.push({ task, file, id, fileExtension });
                        }
                    });
                } catch (error) {
                    console.error('Failed to check existing transcoding tasks:', error);
                    // 检查失败时，所有任务都继续转码流程（保守策略）
                    tasksToTranscode = filesToCheck.map(({ task, file, id, fileExtension }) => ({
                        task,
                        file,
                        id,
                        fileExtension,
                    }));
                }

                // 步骤2: 更新已存在任务的状态到Redux
                if (existingTasks.length > 0) {
                    dispatch(updateTranscodingTask({ updates: existingTasks }));
                }

                // 步骤3: 处理需要转码的任务（上传文件、创建转码任务）
                if (tasksToTranscode.length > 0) {
                    // 并行处理所有需要转码的文件，传递预计算的 id 和 fileExtension 避免重复计算
                    const transcodeResults = await Promise.allSettled(
                        tasksToTranscode.map(({ task, file, id, fileExtension }) =>
                            dispatch(this._processFileWithTranscodingAction(file, task.tempId, id, fileExtension)),
                        ),
                    );

                    const failTasks = transcodeResults.filter(item => item.status === 'rejected');
                    // 如果所有转码任务都失败且没有已存在的任务，则设置为错误状态
                    if (failTasks.length === tasksToTranscode.length && existingTasks.length === 0) {
                        const hasSourceError = failTasks.some(
                            item => item.reason.cause === FJConstant.SOURCE_LOADING_ERROR,
                        );
                        dispatch(
                            setTranscodingStatus(hasSourceError ? FJConstant.SOURCE_LOADING_ERROR : FJConstant.ERROR),
                        );
                    }
                }

                // 步骤4: 统一开始轮询或处理所有任务都已存在的情况
                if (tasksToTranscode.length > 0) {
                    // 有需要转码的任务，统一开始轮询（所有任务创建完成后再轮询）
                    dispatch(this.checkPollingTaskStateAction());
                } else if (existingTasks.length === tempTaskList.length) {
                    // 所有任务都已存在，直接触发完成流程（下载和回调）
                    const downloadTaskList = await dispatch(this.downloadTranscodeFileAction());
                    const { onSuccess = null } = getState().cloudTranscoding;
                    dispatch(setTranscodingStatus(FJConstant.SUCCESS));
                    onSuccess?.(downloadTaskList);
                }
            } else {
                // 如果没有支持转码的文件，直接设置为错误状态
                dispatch(setTranscodingStatus(FJConstant.ERROR));
            }
        };
    },

    /**
     * 检查转码任务状态
     * @param {*} onSuccess 完成回调
     * @param {*} onError 错误回调
     * @returns
     */
    checkPollingTaskStateAction: function () {
        return async (dispatch, getState) => {
            const { transcodingTasks } = getState().cloudTranscoding;
            const pollingTaskStateList = transcodingTasks.filter(item => item.status === FJConstant.RUNNING && item.id);

            if (!f_transcode_isPolling && pollingTaskStateList.length > 0) {
                dispatch(this.pollingTranscodeMediaAction());
            }
        };
    },

    /**
     * 转码轮询
     * @returns {Promise<string>} 输出文件S3链接
     */
    pollingTranscodeMediaAction: function () {
        return async (dispatch, getState) => {
            const { onSuccess = null, onError = null } = getState().cloudTranscoding;
            f_transcode_isPolling = true;
            const _clear = () => {
                f_transcode_isPolling = false;
                if (f_timer) {
                    clearTimeout(f_timer);
                    f_timer = null;
                }
            };
            _clear();

            if (f_cancelled) {
                _clear();
                return;
            }

            const { transcodingTasks } = getState().cloudTranscoding;
            if (transcodingTasks.length === 0) {
                return;
            }

            const pollingTaskStateList = transcodingTasks.filter(item => item.status === FJConstant.RUNNING && item.id);

            if (pollingTaskStateList.length === 0) {
                _clear();
                return;
            }

            const taskIdList = pollingTaskStateList.map(item => item.id);

            let delayTime = normal_time;

            const __pollingFunc = () => {
                dispatch(this.pollingTranscodeMediaAction());
            };

            try {
                let data = await FJNetworkStore.getTaskState(taskIdList.join(','));
                delayTime = normal_time;

                if (!data) {
                    f_errorTime++;
                    if (f_errorTime >= MAX_POLLING_ERROR_TIME) {
                        _clear();
                        dispatch(setTranscodingStatus(FJConstant.ERROR));
                    } else {
                        _clear();
                        f_transcode_isPolling = true;
                        f_timer = setTimeout(__pollingFunc, delayTime);
                    }
                    return;
                }
                if (!Array.isArray(data)) {
                    // 处理数组类型的data
                    data = [data];
                }

                const taskIdStatusList = await Promise.all(
                    data.map(async item => {
                        let taskItem = { id: item.event_id, status: FJConstant.RUNNING, progress: item.progress };
                        const taskData = transcodingTasks.find(task => task.id === item.event_id);
                        switch (item.job_state) {
                            case 'succeeded':
                                taskItem.status = FJConstant.SUCCESS;
                                taskItem.progress = 90; // 90% 表示转码完成，100% 表示下载完成
                                taskItem.downloadUrl =
                                    FJS3Config.S3_NOT_CATCH_HOST +
                                    FJCloudTranscodingConfig.cloudTranscodingExportKey +
                                    item.out_put[0].result;
                                taskItem.original =
                                    FJS3Config.S3_NOT_CATCH_HOST +
                                    FJCloudTranscodingConfig.cloudTranscodingExportKey +
                                    item.out_put[0].original;

                                FJUtil.eaLog('ai_tool_cloud_transcode_success');
                                break;
                            case 'waiting':
                            case 'processing':
                                taskItem.status = FJConstant.RUNNING;
                                taskItem.progress = FJCloudTranscodingUtil.simulateProgress(
                                    taskData.mediaType,
                                    taskData.size,
                                    taskData.progress,
                                );
                                break;
                            case 'canceled':
                                taskItem.status = FJConstant.CANCELLED;
                                break;
                            case 'timeout':
                            case 'failed': {
                                taskItem.status = FJConstant.ERROR;
                                taskItem.progress = 100;
                                FJUtil.eaLog('ai_tool_cloud_transcode_failed', {
                                    tool: FJUtil.getToolName(),
                                    format: taskData.file.type,
                                });
                                break;
                            }
                            default:
                                break;
                        }
                        return taskItem;
                    }),
                );
                // 更新所有任务状态
                dispatch(
                    updateTranscodingTask({
                        updates: taskIdStatusList,
                    }),
                );
                const { transcodingTasks: currentTranscodingTasks = [] } = getState().cloudTranscoding;

                if (currentTranscodingTasks.every(item => item.status !== FJConstant.RUNNING)) {
                    const failTasks = currentTranscodingTasks.filter(item => item.status === FJConstant.ERROR) ?? [];
                    const successTasks =
                        currentTranscodingTasks.filter(item => item.status === FJConstant.SUCCESS) ?? [];
                    _clear();
                    // 下载转码文件
                    const downloadTaskList = await dispatch(this.downloadTranscodeFileAction());

                    if (failTasks.length === 0 && successTasks.length === currentTranscodingTasks.length) {
                        dispatch(setTranscodingStatus(FJConstant.SUCCESS));
                        onSuccess?.(downloadTaskList);
                    } else if (failTasks.length === currentTranscodingTasks.length) {
                        dispatch(setTranscodingStatus(FJConstant.ERROR));
                        onError?.(failTasks, FJConstant.ERROR);
                    } else {
                        dispatch(setTranscodingStatus(FJConstant.PARTIAL_SUCCESS));
                    }
                } else {
                    _clear();
                    f_timer = setTimeout(__pollingFunc, delayTime);
                }
            } catch (error) {
                f_timer = setTimeout(__pollingFunc, error_time);
                // _clear();
            }
        };
    },

    /**
     * 下载转码图片文件
     * @param {string[]} taskIds - 任务ID
     * @returns {Function} Redux thunk action
     * @returns {Array} 转码任务列表
     */
    downloadTranscodeFileAction: function (taskIds) {
        return async (dispatch, getState) => {
            const { transcodingTasks } = getState().cloudTranscoding;
            // 默认下载所有任务的转码文件
            let downloadList = taskIds?.length
                ? transcodingTasks
                      .filter(item => taskIds.includes(item.id))
                      .filter(item => item.status === FJConstant.SUCCESS && item.downloadUrl)
                : transcodingTasks.filter(item => item.status === FJConstant.SUCCESS && item.downloadUrl);

            // 下载转码文件
            const taskList = [];
            try {
                for (let i = 0; i < downloadList.length; i++) {
                    let file = null;
                    switch (downloadList[i].mediaType) {
                        case 'video': {
                            const blob = await FJFileHandleUtil.downloadVideo(downloadList[i].downloadUrl);
                            file = new File([blob], downloadList[i].fileName.split('.')[0] + '.mp4', {
                                type: 'video/mp4',
                            });
                            break;
                        }
                        case 'audio': {
                            const blob = await FJFileHandleUtil.downloadVideo(downloadList[i].downloadUrl);
                            file = new File([blob], downloadList[i].fileName.split('.')[0] + '.wav', {
                                type: 'audio/wav',
                            });
                            break;
                        }
                        case 'image':
                        default: {
                            const blob = await FJFileHandleUtil.downloadImage(downloadList[i].downloadUrl);
                            file = new File([blob], downloadList[i].fileName.split('.')[0] + '.png', {
                                type: 'image/png',
                            });
                            break;
                        }
                    }
                    taskList.push({
                        ...downloadList[i],
                        progress: 100, // 下载完成
                        file,
                    });
                }
            } catch (error) {
                console.error('Download transcoding file error:', error);
                dispatch(setTranscodingStatus(FJConstant.SOURCE_LOADING_ERROR));
                return [];
            }
            // 更新文件
            dispatch(updateTranscodingTask({ updates: taskList }));
            return taskList;
        };
    },

    /**
     * 重试失败任务
     * @returns {Function} Redux thunk action
     */
    retryFailedTasksAction: function () {
        return async (dispatch, getState) => {
            const { transcodingTasks } = getState().cloudTranscoding;
            const failedTasks = transcodingTasks.filter(item => item.status === FJConstant.ERROR);
            if (failedTasks.length === 0) {
                return;
            }
            const updates = failedTasks.map(item => ({
                ...item,
                status: FJConstant.RUNNING,
                progress: 0,
            }));
            dispatch(updateTranscodingTask({ updates }));
            dispatch(setTranscodingStatus(FJConstant.RUNNING));

            // 如果所有任务都创建失败，则返回错误
            // 注意：重试时没有预计算的 id 和 fileExtension，会重新计算
            const result = await Promise.allSettled(
                failedTasks.map((item, index) =>
                    dispatch(this._processFileWithTranscodingAction(failedTasks[index].file, item.tempId)),
                ),
            );
            const failTasks = result.filter(item => item.status === 'rejected');
            if (failTasks.length === failedTasks.length) {
                const hasSourceError = failTasks.some(item => item.reason.cause === FJConstant.SOURCE_LOADING_ERROR);
                dispatch(setTranscodingStatus(hasSourceError ? FJConstant.SOURCE_LOADING_ERROR : FJConstant.ERROR));
            }
        };
    },
};

export default FJActionCloudTranscoding;
